import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import portfolioService from '../../services/portfolioService';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { 
  RefreshCw, DollarSign, TrendingUp, TrendingDown, PieChart, 
  Plus, ArrowRight, Briefcase
} from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Couleurs distinctes pour le Harry Browne Permanent Portfolio
// VTI (Actions) = Bleu, TLT (Obligations LT) = Vert, SHY (Cash) = Jaune, GLD (Or) = Or/Ambre
const COLORS = ['#3B82F6', '#10B981', '#FBBF24', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// Mapping des couleurs par ticker pour le portefeuille permanent
const ASSET_COLORS = {
  'VTI': '#3B82F6',  // Bleu - Actions US
  'TLT': '#10B981',  // Vert - Obligations Long Terme
  'SHY': '#06B6D4',  // Cyan - Cash/T-Bills
  'GLD': '#F59E0B',  // Or/Ambre - Or
};

const PortfolioDashboard = () => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadPortfolios();
  }, []);

  useEffect(() => {
    if (selectedPortfolio) {
      loadMetrics();
      loadAllocation();
    }
  }, [selectedPortfolio]);

  const loadPortfolios = async () => {
    try {
      setIsLoading(true);
      let data = await portfolioService.getAllPortfolios();
      
      if (data.length === 0) {
        await portfolioService.initSamplePortfolio();
        data = await portfolioService.getAllPortfolios();
      }
      
      setPortfolios(data);
      if (data.length > 0) {
        setSelectedPortfolio(data[0].id);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des portfolios');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMetrics = async () => {
    if (!selectedPortfolio) return;
    try {
      const data = await portfolioService.getPortfolioMetrics(selectedPortfolio);
      setMetrics(data);
    } catch (error) {
      console.error('Error loading metrics:', error);
    }
  };

  const loadAllocation = async () => {
    if (!selectedPortfolio) return;
    try {
      const data = await portfolioService.getPortfolioAllocation(selectedPortfolio);
      setAllocation(data.allocation);
    } catch (error) {
      console.error('Error loading allocation:', error);
    }
  };

  const refreshData = () => {
    loadMetrics();
    loadAllocation();
    toast.success('Données rafraîchies');
  };

  const currentPortfolio = portfolios.find(p => p.id === selectedPortfolio);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-manrope">Portfolio Dashboard</h1>
          <p className="text-muted-foreground">Gérez et analysez vos investissements</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={refreshData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Rafraîchir
          </Button>
          <Button size="sm" onClick={() => navigate('/backtest')}>
            Backtester
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Portfolio Info */}
      {currentPortfolio && (
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-primary/20 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold font-manrope">{currentPortfolio.name}</h2>
                  <p className="text-sm text-muted-foreground">{currentPortfolio.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Investissement initial</p>
                <p className="text-2xl font-bold font-mono">
                  ${currentPortfolio.initial_value?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase">Valeur actuelle</span>
              <DollarSign className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold font-mono">
              ${metrics?.current_value?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '—'}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase">Rendement total</span>
              {metrics?.total_return >= 0 ? (
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-400" />
              )}
            </div>
            <p className={`text-2xl font-bold font-mono ${
              metrics?.total_return >= 0 ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {metrics?.total_return >= 0 ? '+' : ''}{metrics?.total_return?.toFixed(2) || '—'}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics?.total_return_amount >= 0 ? '+' : ''}${metrics?.total_return_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '—'}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase">Volatilité</span>
            </div>
            <p className="text-2xl font-bold font-mono">
              {metrics?.volatility?.toFixed(2) || '—'}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">Annualisée</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase">Sharpe Ratio</span>
            </div>
            <p className={`text-2xl font-bold font-mono ${
              metrics?.sharpe_ratio >= 1 ? 'text-emerald-400' : metrics?.sharpe_ratio >= 0 ? 'text-amber-400' : 'text-red-400'
            }`}>
              {metrics?.sharpe_ratio?.toFixed(3) || '—'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics?.sharpe_ratio >= 1 ? 'Excellent' : metrics?.sharpe_ratio >= 0.5 ? 'Bon' : 'À améliorer'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Allocation Chart & Holdings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase flex items-center gap-2">
              <PieChart className="w-4 h-4" />
              Allocation
            </CardTitle>
          </CardHeader>
          <CardContent>
            {allocation && allocation.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={allocation}
                      dataKey="weight"
                      nameKey="ticker"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ ticker, weight }) => `${ticker} ${weight.toFixed(1)}%`}
                    >
                      {allocation.map((entry, index) => (
                        <Cell 
                          key={entry.ticker} 
                          fill={ASSET_COLORS[entry.ticker] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                      formatter={(value, name) => [`${value.toFixed(2)}%`, name]}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                Aucune donnée d'allocation
              </div>
            )}
          </CardContent>
        </Card>

        {/* Holdings List */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase">
              Positions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {allocation?.map((holding, index) => (
                <div key={holding.ticker} className="flex items-center justify-between p-3 bg-background/50 rounded-sm">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: ASSET_COLORS[holding.ticker] || COLORS[index % COLORS.length] }}
                    ></div>
                    <div>
                      <p className="font-medium">{holding.ticker}</p>
                      <p className="text-xs text-muted-foreground">{holding.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-semibold">${holding.value?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs text-muted-foreground">{holding.weight?.toFixed(1)}%</p>
                  </div>
                </div>
              ))}
              
              {(!allocation || allocation.length === 0) && (
                <div className="text-center py-8 text-muted-foreground">
                  <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune position</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Best/Worst Performers */}
      {metrics && (metrics.best_performer || metrics.worst_performer) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {metrics.best_performer && (
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm font-medium text-emerald-400">Meilleure performance</span>
                </div>
                <p className="text-xl font-bold">{metrics.best_performer.ticker}</p>
                <p className="text-2xl font-bold font-mono text-emerald-400">
                  +{metrics.best_performer.return?.toFixed(2)}%
                </p>
              </CardContent>
            </Card>
          )}
          
          {metrics.worst_performer && (
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-5 h-5 text-red-400" />
                  <span className="text-sm font-medium text-red-400">Moins bonne performance</span>
                </div>
                <p className="text-xl font-bold">{metrics.worst_performer.ticker}</p>
                <p className="text-2xl font-bold font-mono text-red-400">
                  {metrics.worst_performer.return?.toFixed(2)}%
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default PortfolioDashboard;
